<template>
	<div class="spe-pdf-dropzone">
		<label v-if="label" class="spe-pdf-dropzone__label" :for="inputId">
			{{ label }}
			<span v-if="required" class="spe-pdf-dropzone__required">*</span>
			<span v-else-if="optionalHint" class="spe-pdf-dropzone__optional">{{ optionalHint }}</span>
		</label>

		<input
			:id="inputId"
			ref="fileInput"
			type="file"
			class="spe-pdf-dropzone__input"
			accept="application/pdf,.pdf"
			:disabled="disabled"
			@change="onNativeChange"
		/>

		<div
			v-if="!selectedFile"
			class="spe-pdf-dropzone__zone"
			:class="{
				'spe-pdf-dropzone__zone--active': dragging,
				'spe-pdf-dropzone__zone--disabled': disabled,
				'spe-pdf-dropzone__zone--error': Boolean(error),
			}"
			role="button"
			tabindex="0"
			:aria-disabled="disabled ? 'true' : 'false'"
			@click="openPicker"
			@keydown.enter.prevent="openPicker"
			@keydown.space.prevent="openPicker"
			@dragenter.prevent="onDragEnter"
			@dragover.prevent="onDragOver"
			@dragleave.prevent="onDragLeave"
			@drop.prevent="onDrop"
		>
			<div class="spe-pdf-dropzone__icon-wrap">
				<i class="pi pi-cloud-upload spe-pdf-dropzone__icon" aria-hidden="true"></i>
			</div>
			<p class="spe-pdf-dropzone__title">{{ emptyTitle }}</p>
			<p class="spe-pdf-dropzone__hint">{{ emptyHint }}</p>
			<span class="spe-pdf-dropzone__browse">Browse PDF</span>
		</div>

		<div v-else class="spe-pdf-dropzone__selected">
			<div class="spe-pdf-dropzone__file-icon" aria-hidden="true">
				<i class="pi pi-file-pdf"></i>
			</div>
			<div class="spe-pdf-dropzone__file-meta">
				<p class="spe-pdf-dropzone__file-name" :title="selectedFile.name">{{ selectedFile.name }}</p>
				<p class="spe-pdf-dropzone__file-size">{{ formatFileSize(selectedFile.size) }} · PDF</p>
			</div>
			<button
				type="button"
				class="spe-pdf-dropzone__replace"
				:disabled="disabled"
				@click="openPicker"
			>
				Replace
			</button>
			<button
				type="button"
				class="spe-pdf-dropzone__remove"
				aria-label="Remove attachment"
				:disabled="disabled"
				@click="clearFile"
			>
				<i class="pi pi-times" aria-hidden="true"></i>
			</button>
		</div>

		<small v-if="error" class="spe-pdf-dropzone__error">{{ error }}</small>
	</div>
</template>

<script setup>
import { computed, ref, watch, defineProps, defineEmits } from 'vue';
import { formatFileSize, validatePdfAttachment } from './speV2PdfAttachment.js';

const props = defineProps({
	modelValue: { type: [File, Object, null], default: null },
	label: { type: String, default: 'Attachment (PDF)' },
	required: { type: Boolean, default: false },
	optionalHint: { type: String, default: '' },
	disabled: { type: Boolean, default: false },
	emptyTitle: { type: String, default: 'Drop PDF here, or browse' },
	emptyHint: { type: String, default: 'PDF only · max 10 MB' },
	inputId: { type: String, default: 'spePdfAttachment' },
	maxBytes: { type: Number, default: undefined },
});

const emit = defineEmits(['update:modelValue', 'error', 'clear']);

const fileInput = ref(null);
const dragging = ref(false);
const localError = ref('');
const dragDepth = ref(0);

const selectedFile = computed(() => props.modelValue || null);
const error = computed(() => localError.value);

watch(
	() => props.modelValue,
	(value) => {
		if (!value) localError.value = '';
	}
);

const openPicker = () => {
	if (props.disabled) return;
	fileInput.value?.click();
};

const applyFile = (file) => {
	const result = validatePdfAttachment(file, { maxBytes: props.maxBytes });
	if (!result.ok) {
		localError.value = result.error;
		emit('error', result.error);
		emit('update:modelValue', null);
		if (fileInput.value) fileInput.value.value = '';
		return;
	}
	localError.value = '';
	emit('update:modelValue', result.file);
};

const clearFile = () => {
	if (props.disabled) return;
	localError.value = '';
	emit('update:modelValue', null);
	emit('clear');
	if (fileInput.value) fileInput.value.value = '';
};

const onNativeChange = (event) => {
	const file = event?.target?.files?.[0] || null;
	if (!file) return;
	applyFile(file);
};

const onDragEnter = () => {
	if (props.disabled) return;
	dragDepth.value += 1;
	dragging.value = true;
};

const onDragOver = () => {
	if (props.disabled) return;
	dragging.value = true;
};

const onDragLeave = () => {
	dragDepth.value = Math.max(0, dragDepth.value - 1);
	if (dragDepth.value === 0) dragging.value = false;
};

const onDrop = (event) => {
	if (props.disabled) return;
	dragDepth.value = 0;
	dragging.value = false;
	const file = event?.dataTransfer?.files?.[0] || null;
	if (!file) return;
	applyFile(file);
};
</script>

<style scoped>
.spe-pdf-dropzone {
	width: 100%;
}

.spe-pdf-dropzone__label {
	display: block;
	font-weight: 600;
	color: #374151;
	margin-bottom: 0.5rem;
	font-size: 0.95rem;
}

.spe-pdf-dropzone__required {
	color: #dc2626;
	margin-left: 0.15rem;
}

.spe-pdf-dropzone__optional {
	font-weight: 500;
	color: #9ca3af;
	margin-left: 0.35rem;
	font-size: 0.85rem;
}

.spe-pdf-dropzone__input {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}

.spe-pdf-dropzone__zone {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.35rem;
	padding: 1.35rem 1rem;
	border: 1.5px dashed #cbd5e1;
	border-radius: 12px;
	background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
	cursor: pointer;
	transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
	outline: none;
	text-align: center;
	user-select: none;
}

.spe-pdf-dropzone__zone:hover,
.spe-pdf-dropzone__zone:focus-visible {
	border-color: #22c55e;
	background: linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 100%);
	box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.12);
}

.spe-pdf-dropzone__zone--active {
	border-color: #16a34a;
	background: #ecfdf5;
	box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.18);
}

.spe-pdf-dropzone__zone--error {
	border-color: #f87171;
	background: #fef2f2;
}

.spe-pdf-dropzone__zone--disabled {
	opacity: 0.6;
	cursor: not-allowed;
	pointer-events: none;
}

.spe-pdf-dropzone__icon-wrap {
	width: 2.75rem;
	height: 2.75rem;
	border-radius: 999px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 1px solid #e2e8f0;
	margin-bottom: 0.25rem;
}

.spe-pdf-dropzone__icon {
	font-size: 1.25rem;
	color: #16a34a;
}

.spe-pdf-dropzone__title {
	margin: 0;
	font-weight: 600;
	color: #1f2937;
	font-size: 0.95rem;
}

.spe-pdf-dropzone__hint {
	margin: 0;
	color: #6b7280;
	font-size: 0.8rem;
}

.spe-pdf-dropzone__browse {
	margin-top: 0.45rem;
	display: inline-flex;
	align-items: center;
	padding: 0.35rem 0.85rem;
	border-radius: 999px;
	background: #ffffff;
	border: 1px solid #d1d5db;
	color: #166534;
	font-size: 0.8rem;
	font-weight: 600;
}

.spe-pdf-dropzone__selected {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.85rem 0.95rem;
	border-radius: 12px;
	border: 1px solid #bbf7d0;
	background: #f0fdf4;
}

.spe-pdf-dropzone__file-icon {
	flex-shrink: 0;
	width: 2.5rem;
	height: 2.5rem;
	border-radius: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #dc2626;
	color: #ffffff;
}

.spe-pdf-dropzone__file-icon .pi {
	font-size: 1.1rem;
}

.spe-pdf-dropzone__file-meta {
	min-width: 0;
	flex: 1;
}

.spe-pdf-dropzone__file-name {
	margin: 0;
	font-weight: 600;
	color: #14532d;
	font-size: 0.9rem;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.spe-pdf-dropzone__file-size {
	margin: 0.15rem 0 0;
	color: #4b5563;
	font-size: 0.78rem;
}

.spe-pdf-dropzone__replace {
	flex-shrink: 0;
	border: 1px solid #86efac;
	background: #ffffff;
	color: #166534;
	border-radius: 8px;
	padding: 0.35rem 0.65rem;
	font-size: 0.78rem;
	font-weight: 600;
	cursor: pointer;
}

.spe-pdf-dropzone__replace:hover:not(:disabled) {
	background: #dcfce7;
}

.spe-pdf-dropzone__remove {
	flex-shrink: 0;
	width: 2rem;
	height: 2rem;
	border-radius: 8px;
	border: 1px solid #fecaca;
	background: #ffffff;
	color: #b91c1c;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
}

.spe-pdf-dropzone__remove:hover:not(:disabled) {
	background: #fef2f2;
}

.spe-pdf-dropzone__replace:disabled,
.spe-pdf-dropzone__remove:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.spe-pdf-dropzone__error {
	display: block;
	margin-top: 0.45rem;
	color: #dc2626;
	font-size: 0.8rem;
}
</style>
